import {trpc} from '@/api/trpcClient';
import {AdyenPlatformExperience, CapitalOverview} from '@adyen/adyen-platform-experience-web';
import '@adyen/adyen-platform-experience-web/adyen-platform-experience-web.css';
import {Trans} from '@lingui/macro';
import {ErrorCode, isFormattedTrpcError} from '@zentact/common';
import {CapitalSessionResponse} from '@zentact/core';
import {Loading, cn} from '@zentact/ui-tailwind';
import {useEffect, useRef, useState} from 'react';

type Props = {
  merchantAccountId?: string;
};

export const CapitalOverviewComponent = ({merchantAccountId}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNotConfiguredMessage, setShowNotConfiguredMessage] = useState(true);
  const createCapitalSession = trpc.capital.createAdyenCapitalSession.useMutation();

  const handleSessionCreate = async (
    merchantAccountId: string
  ): Promise<CapitalSessionResponse> => {
    return await createCapitalSession.mutateAsync({merchantAccountId});
  };

  useEffect(() => {
    let capitalOverview: CapitalOverview | undefined;
    setIsLoading(true);
    setShowNotConfiguredMessage(false);

    const initialize = async () => {
      if (!merchantAccountId) return;

      try {
        const session = await handleSessionCreate(merchantAccountId);
        const core = await AdyenPlatformExperience({
          onSessionCreate: async () => session,
        });
        capitalOverview = new CapitalOverview({core});
        capitalOverview.mount('#capital-overview-container');
      } catch (error) {
        const errorCode = isFormattedTrpcError(error)
          ? error.data.errorCode
          : ErrorCode.ERROR_GENERIC;

        if (errorCode === ErrorCode.MISSING_CAPABILITIES) {
          setShowNotConfiguredMessage(true);
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      capitalOverview?.unmount();
    };
  }, [merchantAccountId]);

  // We don't control how the Adyen Capital Overview component renders.
  // Therefore, we must observe its container for any changes and display
  // the component only once it has fully rendered.
  const checkIsRenderingFinished = (records: MutationRecord[]) => {
    for (const record of records) {
      for (const node of Array.from(record.removedNodes)) {
        if (
          node instanceof HTMLElement &&
          node.className === 'adyen-pe-capital-overview__header-skeleton-container'
        ) {
          setIsLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    const observer = new MutationObserver(checkIsRenderingFinished);
    if (containerRef.current) {
      observer.observe(containerRef.current, {childList: true, subtree: true});
    }
    return () => {
      observer.disconnect();
    };
  }, [containerRef]);

  return (
    <>
      {merchantAccountId?.length ? (
        isLoading ? (
          <div className="flex items-center justify-center h-80">
            <Loading mode="inline" className="pt-52" />
          </div>
        ) : (
          showNotConfiguredMessage && (
            <div className="flex items-center justify-center font-semibold h-80">
              <Trans>
                Merchant account is not configured for capital, please contact support to enable.
              </Trans>
            </div>
          )
        )
      ) : (
        <div className="flex items-center justify-center font-semibold h-80">
          <Trans>No active merchants</Trans>
        </div>
      )}
      <div
        id="capital-overview-container"
        className={cn(
          !isLoading && !showNotConfiguredMessage && 'min-w-full flex justify-center',
          'gap-4 mx-auto mt-4'
        )}
        ref={containerRef}
        hidden={isLoading || showNotConfiguredMessage}
      />
    </>
  );
};
